// Read Aloud Service - ElevenLabs TTS with word timing
// Client-side implementation for reliability with prefetching support

import { logger } from '../../../lib/logger';
import { supabase } from '@/lib/supabase/client';

export interface WordTimestamp {
  word: string;
  start: number;
  end: number;
}

export interface ReadAloudResult {
  audioContent: string; // base64
  mimeType: string;
  timestamps?: WordTimestamp[];
}

// Audio cache for prefetching - keyed by text hash
const audioCache = new Map<string, ReadAloudResult>();
const pendingPrefetches = new Map<string, Promise<ReadAloudResult>>();
const MAX_CACHE_SIZE = 5; // Keep only 5 items in cache to manage memory

// Simple hash function for cache keys
const hashText = (text: string): string => {
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    const char = text.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return hash.toString();
};

// Voice ID for storybook narration (Arabella - children's storybook narrator)
const STORYBOOK_VOICE_ID = 'Z3R5wn05IrDiVCyEkUrK';

// ElevenLabs voice IDs for different story contexts
const VOICE_OPTIONS = {
  // Primary Voice: Arabella - Female, multilingual, clear and expressive
  default: 'Z3R5wn05IrDiVCyEkUrK', // Arabella - children's storybook narrator
  
  // Child-friendly storytelling voices
  storyteller_female: 'Z3R5wn05IrDiVCyEkUrK', // Arabella (Set as primary storyteller)
  storyteller_male: 'pNInz6obpgDQGcFmaJgB', // Adam - clear, engaging
  // Character voices
  young_female: 'jBpfuIE2acCO8z3wKNLl', // Gigi - youthful
  young_male: 'ODq5zmih8GrVes37Dizd', // Patrick - young boy
  elderly_female: 'ThT5KcBeYPX3keUQqHPh', // Dorothy - grandmother
  elderly_male: 'VR6AewLTigWG4xSOukaG', // Arnold - grandfather
  // Animal/creature stories
  playful: 'MF3mGyEYCl7XYWbV9V6O', // Elli - energetic
  gentle: 'XB0fDUnXU5powFXDhCwa', // Charlotte - soft, soothing
};

/**
 * Analyze story content and select appropriate voice
 */
export function selectVoiceForContent(text: string, storyTitle?: string): string {
  // Always use the default female American narrator for consistency
  // Unless specifically overridden for distinct character voices if needed in future
  return VOICE_OPTIONS.default;
}

/**
 * Core function to call ElevenLabs TTS via edge function (server-side API key)
 * Optimized for children's storybook narration:
 * - Lower stability (0.35) = more expressive, emotional reading
 * - Higher style (0.45) = warmer, more engaging tone
 * - Slower speed (0.85) for children's comprehension
 */
async function callElevenLabsAPI(
  text: string,
  voiceId: string
): Promise<ReadAloudResult> {
  const { data, error } = await supabase.functions.invoke('text-to-speech-elevenlabs', {
    body: {
      text,
      voice: voiceId,
      model_id: 'eleven_multilingual_v2',
      voice_settings: {
        stability: 0.35,
        similarity_boost: 0.80,
        style: 0.45,
        use_speaker_boost: true,
        speed: 0.85,
      },
    },
  });

  if (error) {
    logger.error('TTS edge function error:', error);
    throw new Error(`TTS edge function error: ${error.message || error}`);
  }

  if (!data?.audioContent) {
    throw new Error('TTS: no audio content returned from edge function');
  }

  return {
    audioContent: data.audioContent,
    mimeType: data.mimeType || 'audio/mpeg',
    timestamps: undefined
  };
}

/**
 * Manage cache size - remove oldest entries if over limit
 */
function manageCacheSize() {
  while (audioCache.size > MAX_CACHE_SIZE) {
    const firstKey = audioCache.keys().next().value;
    if (firstKey) {
      audioCache.delete(firstKey);
    }
  }
}

/**
 * Prefetch audio for a text (call silently in background)
 * This doesn't throw errors - failures are silent
 */
export async function prefetchAudio(
  text: string,
  storyTitle?: string
): Promise<void> {
  if (!text?.trim()) return;
  
  const cacheKey = hashText(text);
  
  // Already cached or being fetched
  if (audioCache.has(cacheKey) || pendingPrefetches.has(cacheKey)) {
    logger.debug('[TTS] Audio already cached/pending for prefetch');
    return;
  }
  
  logger.debug('[TTS] Prefetching audio in background...');
  
  const selectedVoice = selectVoiceForContent(text, storyTitle);
  
  const fetchPromise = callElevenLabsAPI(text, selectedVoice)
    .then(result => {
      audioCache.set(cacheKey, result);
      manageCacheSize();
      logger.debug('[TTS] Prefetch complete, audio cached');
      return result;
    })
    .catch(err => {
      logger.warn('[TTS] Prefetch failed (silent):', err.message);
      throw err; // Re-throw so Promise rejects
    })
    .finally(() => {
      pendingPrefetches.delete(cacheKey);
    });
  
  pendingPrefetches.set(cacheKey, fetchPromise);
  
  // Don't await - let it run in background
}

/**
 * Check if audio is cached for a text
 */
export function isAudioCached(text: string): boolean {
  const cacheKey = hashText(text);
  return audioCache.has(cacheKey);
}

/**
 * Generate speech audio for text using ElevenLabs (client-side)
 * Uses cache if available, otherwise fetches fresh
 */
export async function generateSpeech(
  text: string,
  voiceId?: string,
  storyTitle?: string
): Promise<ReadAloudResult> {
  try {
    const cacheKey = hashText(text);
    
    // Check cache first
    if (audioCache.has(cacheKey)) {
      logger.debug('[TTS] Using cached audio');
      return audioCache.get(cacheKey)!;
    }
    
    // Check if prefetch is in progress
    if (pendingPrefetches.has(cacheKey)) {
      logger.debug('[TTS] Waiting for pending prefetch...');
      return await pendingPrefetches.get(cacheKey)!;
    }
    
    // Generate fresh
    const selectedVoice = voiceId || selectVoiceForContent(text, storyTitle);
    logger.debug('[TTS] Generating fresh audio, voice:', selectedVoice);
    
    const result = await callElevenLabsAPI(text, selectedVoice);
    
    // Cache the result
    audioCache.set(cacheKey, result);
    manageCacheSize();
    
    return result;
  } catch (err) {
    logger.error('Error generating speech:', err);
    throw err;
  }
}

/**
 * Generate speech with word-level timestamps for highlighting
 * Uses client-side ElevenLabs call with estimated timestamps
 */
export async function generateSpeechWithTiming(
  text: string,
  voiceId?: string,
  storyTitle?: string
): Promise<ReadAloudResult> {
  try {
    // Generate speech first
    const result = await generateSpeech(text, voiceId, storyTitle);
    
    // Estimate timestamps based on text
    // For MP3 at 128kbps: duration ≈ fileSize(bytes) * 8 / 128000
    const audioBytes = atob(result.audioContent).length;
    const estimatedDuration = (audioBytes * 8) / 128000;
    
    // Generate estimated word timestamps
    const timestamps = estimateTimestamps(text, estimatedDuration);

    return {
      ...result,
      timestamps
    };
  } catch (err) {
    logger.error('Error generating speech with timing:', err);
    throw err;
  }
}

/**
 * Convert base64 audio to a playable Blob URL
 */
export function createAudioUrl(base64Audio: string, mimeType: string = 'audio/mpeg'): string {
  const byteCharacters = atob(base64Audio);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: mimeType });
  return URL.createObjectURL(blob);
}

/**
 * Parse text into words while preserving punctuation positions
 */
export function parseTextIntoWords(text: string): { word: string; index: number }[] {
  const words: { word: string; index: number }[] = [];
  const regex = /\b[\w']+\b/g;
  let match;
  
  while ((match = regex.exec(text)) !== null) {
    words.push({
      word: match[0],
      index: match.index
    });
  }
  
  return words;
}

/**
 * Estimate word timestamps based on text length and audio duration
 * This is a fallback when actual timestamps aren't available
 */
export function estimateTimestamps(
  text: string,
  audioDuration: number
): WordTimestamp[] {
  const words = parseTextIntoWords(text);
  if (words.length === 0) return [];
  
  // Estimate based on word length (longer words take more time)
  const totalLength = words.reduce((sum, w) => sum + w.word.length, 0);
  let currentTime = 0;
  
  return words.map(({ word }) => {
    const wordDuration = (word.length / totalLength) * audioDuration * 0.9; // 90% for speech, 10% for pauses
    const timestamp = {
      word,
      start: currentTime,
      end: currentTime + wordDuration
    };
    currentTime += wordDuration + (audioDuration * 0.1 / words.length); // Add pause
    return timestamp;
  });
}
